<template>
  <StSlider
    class="lock-events"
    data-t="lock-events"
    :scroll-step="214"
    :arrow-offset-vertical="2"
    :items-gap="8"
  >
    <StSliderItem v-for="leftEvent in leftSectionEvents" :key="leftEvent.id">
      <LockEventsItem
        :event="leftEvent"
        :locked="isLocked(leftEvent.id)"
        @toggle="handleToggle(leftEvent.id)"
      />
    </StSliderItem>
    <div v-if="leftSectionEvents.length" class="separator" />
    <StSliderItem v-for="rightEvent in rightSectionEvents" :key="rightEvent.id">
      <LockEventsItem
        :event="rightEvent"
        :locked="isLocked(rightEvent.id)"
        @toggle="handleToggle(rightEvent.id)"
      />
    </StSliderItem>
  </StSlider>
</template>

<script setup lang="ts">
import type { TSportEvent } from 'markets-store'
import { setBy, uniqBy } from '@st/utils'
import { useLockEventsStore } from '../../stores/useLockEventsStore'

const LOCK_EVENTS_QUANTITY = 10

const props = withDefaults(
  defineProps<{
    /* В росписи сюда прокидывается открытое событие. Оно попадает в начало списка */
    event?: TSportEvent
    /* В зависимости от режима будет отличаться набор событий */
    mode?: 'top' | 'live' | 'cyber'
  }>(),
  {
    event: undefined,
    mode: 'top',
  },
)

const lockEventsStore = useLockEventsStore()
const { lockedEvents } = storeToRefs(lockEventsStore)
const { addLockedEvent, removeLockedEvent, isLocked } = lockEventsStore

function handleToggle(eventId: number) {
  if (isLocked(eventId)) {
    removeLockedEvent(eventId)
  } else {
    addLockedEvent(eventId)
  }
}

const { topEvents } = useTopEvents({ quantity: LOCK_EVENTS_QUANTITY })
const { topLiveEvents } = useTopLiveEvents({ quantity: LOCK_EVENTS_QUANTITY })
const { topCyberEvents } = useTopCyberEvents({ quantity: LOCK_EVENTS_QUANTITY })

const topEventsInCurrentMode = computed(() => {
  switch (props.mode) {
    case 'live':
      return topLiveEvents.value
    case 'cyber':
      return topCyberEvents.value
    default:
      return topEvents.value
  }
})

const leftSectionEvents = computed(() => {
  const result: TSportEvent[] = []

  if (props.event) result.push(props.event)
  result.push(...lockedEvents.value)

  return uniqBy(result, (event) => event.id)
})

const rightSectionEvents = computed<TSportEvent[]>(() => {
  const leftSectionEventsIds = setBy(
    leftSectionEvents.value,
    (event) => event.id,
  )
  return topEventsInCurrentMode.value.filter(
    (event) => !leftSectionEventsIds.has(event.id),
  )
})
</script>

<style scoped>
.lock-events {
  display: flex;

  :deep(.st-slider-items) {
    align-items: center;
  }
}

.separator {
  position: relative;
  display: flex;
  align-items: center;

  &::after {
    content: '';
    height: 28px;
    border: 1px solid var(--border-secondary);
  }
}
</style>
